import React from 'react';
import {Slot} from '@radix-ui/react-slot';
import {cva, type VariantProps} from 'class-variance-authority';

import {cn} from '@/lib/utils';
import {LoadingSpinner} from './loading-spinner';
import {SlotChild} from './slot-child';

const buttonVariants = cva(
    [
        'inline-flex items-center justify-center whitespace-nowrap rounded-md',
        'gap-2',
        'font-medium',
        'ring-offset-background transition-colors',
        'focus-visible:outline-none focus-visible:ring-2 focus-visible:ring-ring focus-visible:ring-offset-2',
        'disabled:pointer-events-none disabled:opacity-50',
    ],
    {
        variants: {
            variant: {
                default: 'uppercase',
                outline: 'uppercase border bg-transparent',
                ghost: 'uppercase hover:bg-accent hover:text-accent-foreground',
                link: 'underline-offset-4 hover:underline font-normal',
            },
            color: {
                default: 'text-muted-foreground',
                destructive: 'hover:bg-destructive/90',
                success: 'hover:bg-success/90',
                warning: 'hover:bg-warning/90',
                info: 'hover:bg-info/90',
                primary: 'hover:bg-primary/90',
                secondary: 'hover:bg-secondary/80',
            },
            size: {
                default: 'px-4 py-2',
                sm: 'px-3 py-1 text-sm',
                lg: 'px-4 py-2 text-lg',
                icon: 'h-10 w-10',
            },
        },
        compoundVariants: [
            {
                variant: 'default',
                color: 'primary',
                className: 'bg-primary hover:bg-primary/90 text-primary-foreground',
            },
            {
                variant: 'default',
                color: 'secondary',
                className: 'bg-secondary hover:bg-secondary/90 text-secondary-foreground',
            },
            {
                variant: 'default',
                color: 'destructive',
                className: 'bg-destructive hover:bg-destructive/90 text-destructive-foreground',
            },
            {
                variant: 'default',
                color: 'success',
                className: 'bg-success hover:bg-success/90 text-success-foreground',
            },
            {
                variant: 'default',
                color: 'warning',
                className: 'bg-warning hover:bg-warning/90 text-warning-foreground',
            },
            {
                variant: 'default',
                color: 'info',
                className: 'bg-info hover:bg-info/90 text-info-foreground',
            },
            {
                variant: 'outline',
                color: 'default',
                className: 'border-accent-foreground text-accent-foreground hover:bg-accent/90',
            },
            {
                variant: 'outline',
                color: 'primary',
                className: 'border-primary text-primary hover:text-primary-foreground hover:bg-primary/90',
            },
            {
                variant: 'outline',
                color: 'secondary',
                className:
                    'border-secondary text-secondary hover:bg-secondary/80 data-[state="open"]:bg-secondary/80 data-[state="open"]:text-secondary-foreground',
            },
            {
                variant: 'outline',
                color: 'destructive',
                className:
                    'border-destructive text-destructive hover:bg-destructive/90 data-[state="open"]:bg-destructive/90 data-[state="open"]:text-destructive-foreground',
            },
            {
                variant: 'outline',
                color: 'success',
                className:
                    'border-success text-success hover:bg-success/90 data-[state="open"]:bg-success/90 data-[state="open"]:text-success-foreground',
            },
            {
                variant: 'outline',
                color: 'warning',
                className:
                    'border-warning text-warning hover:bg-warning/90 data-[state="open"]:bg-warning/90 data-[state="open"]:text-warning-foreground',
            },
            {
                variant: 'outline',
                color: 'info',
                className:
                    'border-info text-info hover:bg-info/90 data-[state="open"]:bg-info/90 data-[state="open"]:text-info-foreground',
            },
            {
                variant: 'ghost',
                color: 'default',
                className:
                    'hover:bg-accent/90 data-[state="open"]:bg-accent/90 data-[state="open"]:text-accent-foreground',
            },
            {
                variant: 'ghost',
                color: 'primary',
                className:
                    'text-primary hover:bg-primary/90 data-[state="open"]:bg-primary/90 data-[state="open"]:text-primary-foreground',
            },
            {
                variant: 'ghost',
                color: 'secondary',
                className:
                    'text-secondary hover:bg-secondary/90 data-[state="open"]:bg-secondary/90 data-[state="open"]:text-secondary-foreground',
            },
            {
                variant: 'ghost',
                color: 'destructive',
                className:
                    'text-destructive hover:bg-destructive/90 data-[state="open"]:bg-destructive/90 data-[state="open"]:text-destructive-foreground',
            },
            {
                variant: 'ghost',
                color: 'success',
                className:
                    'text-success hover:bg-success/90 data-[state="open"]:bg-success/90 data-[state="open"]:text-success-foreground',
            },
            {
                variant: 'ghost',
                color: 'warning',
                className:
                    'text-warning hover:bg-warning/90 data-[state="open"]:bg-warning/90 data-[state="open"]:text-warning-foreground',
            },
            {
                variant: 'ghost',
                color: 'info',
                className:
                    'text-info hover:bg-info/90 data-[state="open"]:bg-info/90 data-[state="open"]:text-info-foreground',
            },
            {
                variant: 'link',
                color: 'default',
                className: '',
            },
            {
                variant: 'link',
                color: 'primary',
                className: 'text-primary',
            },
            {
                variant: 'link',
                color: 'secondary',
                className: 'text-secondary',
            },
            {
                variant: 'link',
                color: 'destructive',
                className: 'text-destructive',
            },
            {
                variant: 'link',
                color: 'success',
                className: 'text-success',
            },
            {
                variant: 'link',
                color: 'warning',
                className: 'text-warning',
            },
            {
                variant: 'link',
                color: 'info',
                className: 'text-info',
            },
            {
                variant: 'link',
                color: 'secondary',
                className: 'hover:text-accent',
            },
            {
                variant: 'link',
                color: 'destructive',
                className: 'hover:text-accent',
            },
        ],
        defaultVariants: {
            variant: 'default',
            color: 'default',
            size: 'default',
        },
    },
);

export interface ButtonProps
    extends Omit<React.ButtonHTMLAttributes<HTMLButtonElement>, 'color'>, VariantProps<typeof buttonVariants> {
    asChild?: boolean;
    loading?: boolean;
    loadingText?: string;
}

const Button = React.forwardRef<HTMLButtonElement, ButtonProps>(
    (
        {className, variant, color, size, asChild = false, loading = false, loadingText, children, disabled, ...props},
        ref,
    ) => {
        const Comp = asChild ? Slot : 'button';

        return (
            <Comp
                className={cn(buttonVariants({variant, size, color, className}))}
                ref={ref}
                {...props}
                disabled={disabled || loading}
            >
                <SlotChild asChild={asChild} child={children}>
                    {(child) => (
                        <>
                            {loading && <LoadingSpinner />}
                            {loading ? (loadingText ?? child) : child}
                        </>
                    )}
                </SlotChild>
            </Comp>
        );
    },
);
Button.displayName = 'Button';

export {Button, buttonVariants};
